/*
 * Merged gift certificate page.
 *
 * The physical and digital gift certificates are two separate Shopify products
 * sold through one page. This component owns the synthetic "Type" option,
 * resolves Type x Denomination to a real variant id, and gates add to cart.
 *
 * All product data is inlined by the section as JSON — nothing is fetched, so
 * switching type or denomination is instant and costs no requests.
 *
 * Scope: rendered inside the theme's `xProductCart` component, so `addToCart`
 * is inherited by the buy button's expression. Alpine chains data scopes, which
 * is the same arrangement the theme uses for its own gift certificate terms.
 */
if (!window.Eurus.loadedScript.has('gift-certificate.js')) {
  window.Eurus.loadedScript.add('gift-certificate.js');

  document.addEventListener('alpine:init', () => {
    Alpine.data('xGiftCard', (sectionId) => ({
      /* Shaped so the bindings that read it are safe on the first paint,
         before init() has parsed the inlined data. */
      config: { types: {}, amounts: [], priceRange: '', strings: { addToCart: '', errors: {} } },

      /* Neither is chosen on load — the customer picks both. */
      type: null,
      amount: null,
      quantity: 1,
      termsAgreed: false,

      /* True once add to cart has been attempted, so errors stay quiet until then. */
      attempted: false,
      announcement: '',

      init() {
        this._keepBuyButtonLive();

        const source = document.getElementById(`gift-card-data-${sectionId}`);
        if (!source) return;

        this.config = JSON.parse(source.textContent);
        this._restoreFromUrl();
      },

      /* ---------------------------------------------------------------- state */

      get amounts() {
        return this.config.amounts || [];
      },

      get variant() {
        if (!this.type || !this.amount) return null;
        const amount = this.amounts.find((entry) => entry.key === this.amount);
        return (amount && amount.variants[this.type]) || null;
      },

      get hasType() {
        return this.type !== null;
      },

      /* Falls back to the range rather than to nothing: the price line is always
         on screen, so that choosing a denomination changes a number in place
         instead of inserting a block and pushing the column down. */
      get price() {
        return this.variant ? this.variant.price : this.config.priceRange;
      },

      get compareAtPrice() {
        return this.variant && this.variant.compareAt ? this.variant.compareAt : '';
      },

      get isDigital() {
        return this.type === 'digital';
      },

      /*
       * The single reason add to cart is blocked, in the order the customer
       * meets them going down the page. Drives both the error shown and which
       * control receives focus.
       */
      get blocker() {
        if (!this.type) return 'type';
        if (!this.amount) return 'amount';
        if (!this.variant || !this.variant.available) return 'unavailable';
        if (!this.termsAgreed) return 'terms';
        return null;
      },

      get canAdd() {
        return this.blocker === null;
      },

      get addToCartLabel() {
        const base = this.config.strings.addToCart;
        return this.variant ? `${base} - ${this.variant.price}` : base;
      },

      /* Whether a denomination exists and is in stock for the chosen type. */
      isAmountAvailable(key) {
        if (!this.type) return true;

        const amount = this.amounts.find((entry) => entry.key === key);
        const variant = amount && amount.variants[this.type];
        return Boolean(variant && variant.available);
      },

      isTypeAvailable(key) {
        const type = this.config.types[key];
        return Boolean(type && type.available);
      },

      showError(field) {
        return this.attempted && this.blocker === field;
      },

      /* --------------------------------------------------------------- actions */

      selectType(key) {
        if (this.type === key) return;

        if (!this.isTypeAvailable(key)) {
          this.announcement = this.config.strings.errors.unavailable;
          return;
        }

        this.type = key;

        // A denomination the new type doesn't carry can't stay selected.
        if (this.amount && !this.isAmountAvailable(this.amount)) {
          this.amount = null;
        }

        if (!this.isDigital) this._resetRecipient();

        this._announceSelection();
      },

      selectAmount(key) {
        if (this.amount === key) return;

        if (!this.isAmountAvailable(key)) {
          this.announcement = this.config.strings.errors.unavailable;
          return;
        }

        this.amount = key;
        this._announceSelection();
      },

      /* Clamped here rather than relying on min/step alone, so a typed or
         pasted value can never post a zero or negative quantity. */
      setQuantity(value) {
        const parsed = parseInt(value, 10);
        this.quantity = Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
      },

      /*
       * Called when the buy button is pressed while blocked. The button keeps
       * `aria-disabled` rather than the `disabled` attribute so it stays
       * focusable and can explain itself here — a hard-disabled button gives
       * keyboard and screen reader users a dead end with no reason.
       */
      flagBlocked() {
        this.attempted = true;

        const target = {
          type: `gift-card-type-${sectionId}`,
          amount: `gift-card-amount-${sectionId}`,
          terms: `gift-card-terms-${sectionId}`,
        }[this.blocker];

        this.announcement = this.config.strings.errors[this.blocker] || '';

        if (!target) return;

        this.$nextTick(() => {
          const group = document.getElementById(target);
          if (!group) return;

          const focusable = group.querySelector('input:not([disabled])');
          group.scrollIntoView({ behavior: 'smooth', block: 'center' });
          if (focusable) focusable.focus({ preventScroll: true });
        });
      },

      /* --------------------------------------------------------------- private */

      /*
       * Add to cart stays a live control on this page: it is never disabled, and
       * pressing it while something is missing explains what (see flagBlocked).
       *
       * The theme's own `gift-card-recipient.js` disagrees — it puts a real
       * `disabled` attribute on `.add_to_cart_button` whenever the gift email is
       * empty or invalid, which takes the button out of the tab order and leaves
       * it silent. That component is shared with every other product page and
       * carries Shopify's recipient contract, so it is not forked; the attribute
       * is stripped back off here instead. Its own inline field errors still say
       * what is wrong, and `blocker` has no opinion on the recipient fields, so
       * nothing is lost.
       *
       * Removing an absent attribute records no mutation, so the observer cannot
       * feed itself.
       */
      _keepBuyButtonLive() {
        const button = document.getElementById(`gift-card-atc-${sectionId}`);
        if (!button) return;

        const strip = () => button.removeAttribute('disabled');

        strip();
        new MutationObserver(strip).observe(button, { attributeFilter: ['disabled'] });
      },

      /*
       * A link to either product carries ?variant=. The redirect preserves it,
       * so a customer arriving from an old product URL lands on their choice
       * already made.
       */
      _restoreFromUrl() {
        const requested = new URLSearchParams(window.location.search).get('variant');
        if (!requested) return;

        this.amounts.forEach((amount) => {
          Object.keys(amount.variants).forEach((type) => {
            if (String(amount.variants[type].id) !== requested) return;

            this.type = type;
            this.amount = amount.key;
          });
        });
      },

      /*
       * Switching away from digital must clear the gift recipient form, or its
       * properties would ride along on a physical variant. Dispatching `change`
       * lets the theme's own recipient component do its normal teardown.
       */
      _resetRecipient() {
        const checkbox = document.getElementById(`Recipient-checkbox-${sectionId}`);
        if (!checkbox || !checkbox.checked) return;

        checkbox.checked = false;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
      },

      /* Spoken by the polite live region; visually the page already shows it. */
      _announceSelection() {
        if (this.attempted && this.canAdd) this.attempted = false;

        if (!this.variant) {
          this.announcement = this.type ? this.config.types[this.type].label : '';
          return;
        }

        this.announcement = `${this.config.types[this.type].label}, ${this.variant.price}`;
      },
    }));
  });
}
